// The public shared logbook-trip view: /shared/logbook/:shareId, a sibling of
// /shared/trip and of /support -- NOT under /me, so anyone holding the link can
// open it with no account.
//
// Nothing on these pages reads the database: every table is userPool-authed and
// would come back silently empty for a signed-out visitor. The one fetch goes
// to the share-logbook-trip Lambda's public Function URL
// (src/utilities/shareLogbookTrip.mjs), which validates the unguessable share
// id and returns {logbookType, entries, places, photos} -- the places being the
// gazetteer rows the routes reference, and the photos being PRESIGNED S3 URLs,
// since the anonymous page can look neither up itself.
//
// This component owns the fetch and the derived display state; the entries
// table and the per-entry view are nested routes sharing it via Outlet context,
// so a deep link to an entry works and stepping into one costs no second fetch.
//
// NEARLY read-only, and the exception is worth stating precisely because the rest
// of the rule is still enforced by OMISSION: no Rename, no Combine, no delete, and
// the resolved display copies are never written back.
//
// THE ONE WRITE IS ADDING A PHOTO. A visitor can attach their own photos to a day
// of the trip, through the share-upload Lambda's public Function URL
// (src/utilities/shareUpload.mjs) -- never through AppSync and never through
// Amplify Storage, neither of which an anonymous page has. It writes nothing about
// the trip itself: no field, no date, no name, no route. It is gated twice, on the
// deploy (`shareUploadAvailable()`) and on the owner's per-link toggle
// (`payload.uploadsAllowed`), and the photos land in the entry's ordinary folder so
// the owner can delete any of them from their own page.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Outlet, useOutletContext } from 'react-router-dom';
import { Spinner } from '../../components';
import {
  fetchSharedLogbookTrip,
  shareLogbookTripAvailable,
} from '../../utilities/shareLogbookTrip.mjs';
// From fleet-shared, deliberately not src/utilities/places.mjs: that one
// imports Database.mjs, and nothing on this route may pull in the Amplify
// client.
import { indexPlaces } from '../../fleet-shared/PlaceFields.mjs';
import { sortTripsAscending } from '../../fleet-shared/TripFields.mjs';
import { utcDateKey } from '../../fleet-shared/TripGroups.mjs';
import { zipFileName } from '../../utilities/photoZip.mjs';
import {
  listColumnsForType,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import {
  tripFieldForType,
  tripNameOf,
} from '../../fleet-shared/LogbookTrips.mjs';
import { useEntryMapLegs } from '../logbook/useEntryMapLegs';
import { useDocumentTitle } from '../../utilities/useDocumentTitle';

export function useSharedLogbookTrip() {
  return useOutletContext();
}

const SharedLogbookTrip = () => {
  const { shareId } = useParams();

  // 'loading' | 'gone' | 'error' | 'ok'
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState(null);

  // The two view switches. Local state seeded ON/ON, deliberately NOT
  // useLogbookViewPrefs: that is the OWNER's localStorage, and its showNotes
  // default is false. A visitor was sent this link to look at the trip, so both
  // are on, and their choice here is not written anywhere.
  const [showNotes, setShowNotes] = useState(true);
  const [showPhotos, setShowPhotos] = useState(true);

  useEffect(() => {
    // A build without the share backend can still be handed a share URL;
    // "gone" is the honest answer there too (nothing can be looked up).
    if (!shareLogbookTripAvailable()) {
      setStatus('gone');
      return undefined;
    }
    let mounted = true;
    setStatus('loading');
    setPayload(null);
    fetchSharedLogbookTrip(shareId)
      .then((result) => {
        if (!mounted) return;
        if (result.status === 'gone') {
          setStatus('gone');
        } else {
          setPayload(result);
          setStatus('ok');
        }
      })
      .catch(() => {
        if (mounted) setStatus('error');
      });
    return () => { mounted = false; };
  }, [shareId]);

  // Splice photos a visitor has just added into what we already hold.
  //
  // DELIBERATELY NOT A REFETCH. `complete` hands back presigned GETs for exactly
  // the new photos, and the alternative -- calling fetchSharedLogbookTrip again --
  // re-presigns EVERY photo on the page, which changes every URL and makes the
  // browser re-download every thumbnail it is already showing. `photoTotals` is
  // bumped alongside so the "N more aren't shown" arithmetic stays right.
  const addPhotos = useCallback((entryId, photos) => {
    if (!photos?.length) return;
    setPayload((current) => {
      if (!current) return current;
      const group = current.photos?.[entryId] ?? { total: 0, items: [] };
      return {
        ...current,
        // photosOk stays whatever it was: a listing that failed earlier has not
        // started working, and pretending otherwise would hide the warning.
        photos: {
          ...current.photos,
          [entryId]: {
            total: (group.total ?? 0) + photos.length,
            items: [...(group.items ?? []), ...photos],
          },
        },
      };
    });
  }, []);

  const typeId = payload?.logbookType || LOGBOOK_TYPE_MAINTENANCE;

  // The returned place rows, indexed the way every resolver expects.
  const placeIndex = useMemo(
    () => indexPlaces(payload?.places ?? []),
    [payload]
  );

  // The Lambda already ordered these oldest-first, but sorting again is one
  // line and keeps the page from depending on the wire order. Oldest-first is
  // the MAP's order (the End pill hangs on the last leg) and the heading's;
  // the table takes `newestFirst`, matching the owner's page.
  const orderedEntries = useMemo(
    () => (payload ? sortTripsAscending(payload.entries) : null),
    [payload]
  );

  // The display copies, the TripGroupMap adapter and the lazy-chunk gate, all
  // from the owner page's own hook -- so a fix to any of the three lands here
  // too. It imports only LogbookTypes and TripRoute; no Amplify.
  const { newestFirst, mapLegs, anyOnMap } = useEntryMapLegs(orderedEntries, placeIndex);

  // THE LAMBDA'S ANSWER, not ours. summarizeTripEntries needs
  // `includeInSummary` to know which entries count, and that flag is withheld
  // from a public share (LogbookTypes' publicFieldsForType) -- so recomputing
  // here would not be a second opinion, it would be the wrong number: the
  // default is INCLUDED, so an entry the owner excluded would start counting.
  // A payload with no summary draws no totals line at all, which is what
  // `summary && summary.count > 0` downstream already means.
  const summary = payload?.summary ?? null;

  // Trip Name is redundant inside one trip -- it is the page heading -- so the
  // column comes out, exactly as on the owner's page.
  const columns = useMemo(() => {
    const tripField = tripFieldForType(typeId);
    return listColumnsForType(typeId).filter((field) => field.key !== tripField?.key);
  }, [typeId]);

  // The trip's name, from the entries: a 'g:' key carries none (that is the
  // point of it), and the URL is the only other thing this page has. The
  // first-seen spelling, the same label the owner's page shows.
  const name = useMemo(() => {
    if (!orderedEntries?.length) return 'Trip';
    return tripNameOf(orderedEntries[0], typeId).trim() || 'Trip';
  }, [orderedEntries, typeId]);

  // `null` until answered, a Map once answered -- the useEntryPhotos rule,
  // carried over the wire as `photosOk`. Collapse the two and a trip whose
  // photos could not be listed reads as one whose entries have none.
  const photosByEntry = useMemo(() => {
    if (!payload || !payload.photosOk) return null;
    return new Map(
      Object.entries(payload.photos).map(([entryId, group]) => [entryId, group.items ?? []])
    );
  }, [payload]);

  // How many photos each entry really has, before the Lambda's cap. The strip's
  // "+N more" is built from the list it was given, so the entry page uses this
  // to say when there are more than it was sent.
  const photoTotals = useMemo(() => {
    if (!payload) return null;
    return new Map(
      Object.entries(payload.photos ?? {}).map(([entryId, group]) => [entryId, group.total ?? 0])
    );
  }, [payload]);

  // PhotoErrorAlert's shape, so this page uses the same warning and the same
  // dismissal as the owner's. Always `list`: the only thing that can fail here
  // is the Lambda's S3 listing, which arrives as `photosOk: false` -- there is
  // no per-photo open request to fail, because a dead presigned URL is
  // AttachmentImage's own inline glyph.
  //
  // MEMOIZED, and that is not a micro-optimisation: the dismissal keys on the
  // error's IDENTITY, so a fresh object each render would reopen the alert the
  // moment anything else on the page changed.
  const photosError = useMemo(
    () => (payload && !payload.photosOk ? { kind: 'list' } : null),
    [payload]
  );

  // WHAT "DOWNLOAD ALL" MEANS ON THIS PAGE, computed once here so both surfaces
  // report the same numbers.
  //
  // `groups` is oldest-first with a `YYYY-MM-DD` folder per entry, because two
  // days of a trip routinely both hold IMG_1234.jpg and a flat pile of two
  // hundred photos is not an archive anyone can use.
  //
  // `withheld` is the honest part. The Lambda presigns at most
  // MAX_SHARED_ENTRY_PHOTOS per entry and MAX_SHARED_PHOTOS per trip, so on a
  // big trip "all" is genuinely not all -- and a zip quietly holding 240 of 271
  // photos is worse than one that says so. `photoTotals` is the count BEFORE
  // those caps, which is exactly what makes the difference computable.
  const photosForDownload = useMemo(() => {
    if (!orderedEntries || !photosByEntry) return { groups: [], withheld: 0 };
    const groups = [];
    let published = 0;
    for (const entry of orderedEntries) {
      const photos = photosByEntry.get(entry.id) ?? [];
      if (photos.length === 0) continue;
      published += photos.length;
      groups.push({ folder: utcDateKey(entry.date) ?? entry.id, photos });
    }
    let total = 0;
    for (const count of (photoTotals ?? new Map()).values()) total += count;
    return { groups, withheld: Math.max(0, total - published) };
  }, [orderedEntries, photosByEntry, photoTotals]);

  const zipName = useMemo(() => zipFileName(name), [name]);

  useDocumentTitle(status === 'ok' ? `${name} - TaskPogo` : 'Shared Trip - TaskPogo');

  if (status === 'loading') {
    return <Spinner />;
  }

  if (status === 'gone') {
    return (
      <p className="text-center text-body-secondary py-5">
        This shared trip is no longer available.
      </p>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-center text-body-secondary py-5">
        We couldn&apos;t load this trip. Please try again later.
      </p>
    );
  }

  return (
    <div className="pb-2">
      <Outlet
        context={{
          shareId,
          name,
          typeId,
          columns,
          summary,
          // Newest-first: the entries table reads it and the entry page only
          // finds its row by id, so the table's order is the one to publish.
          displayEntries: newestFirst,
          mapLegs,
          anyOnMap,
          placeIndex,
          photosByEntry,
          photoTotals,
          photosError,
          photosForDownload,
          zipName,
          // Oldest-first and un-reversed, because the upload card's day picker
          // reads chronologically. `displayEntries` is newest-first for the table.
          orderedEntries,
          // The owner's per-link toggle, as the Lambda's boolean rather than the
          // raw column -- nullish means on, and that rule has one reader.
          uploadsAllowed: payload?.uploadsAllowed === true,
          addPhotos,
          showNotes,
          setShowNotes,
          showPhotos,
          setShowPhotos,
        }}
      />
    </div>
  );
};

export default SharedLogbookTrip;
